// Genuine Real-time Analytics Tracker for Coveristic Search Console & Traffic

export interface RealtimeQueryStat {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  lastSearched: string;
}

export interface RealtimePageStat {
  path: string;
  name: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  lastVisited: string;
}

export interface RealtimeDayStat {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  visitors: number;
  whatsAppOrders?: number;
}

export interface RealtimeActivityEvent {
  id: string;
  timestamp: string;
  type: 'page_view' | 'product_click' | 'category_filter' | 'search' | 'whatsapp_order';
  title: string;
  details: string;
  device: string;
  city: string;
}

export interface RealtimeSessionUser {
  id: string;
  city: string;
  country: string;
  flag: string;
  device: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  page: string;
  pageName: string;
  referrerQuery: string;
  timeOnSite: string;
  activeAction: string;
  lastActiveTimestamp: number;
}

export interface RealtimeAnalyticsStore {
  totalClicks: number;
  totalImpressions: number;
  totalVisitors: number;
  totalWhatsAppOrders: number;
  lastResetAt: string;
  queries: RealtimeQueryStat[];
  pages: RealtimePageStat[];
  days: RealtimeDayStat[];
  deviceClicks: {
    mobile: number;
    desktop: number;
    tablet: number;
  };
  countryVisitors: {
    [countryCode: string]: {
      country: string;
      code: string;
      flag: string;
      visitors: number;
      clicks: number;
      impressions: number;
    };
  };
  recentEvents: RealtimeActivityEvent[];
}

const STORAGE_KEY = 'coveristic_realtime_analytics_v4';
const SESSIONS_STORAGE_KEY = 'coveristic_active_sessions_v4';
const SESSION_USER_KEY = 'coveristic_analytics_session_id_v4';

// Helper to detect current user's device
export function detectDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|blackberry|opera mini|iemobile|wpdesktop/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

export function detectDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Desktop Browser';
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone (Safari)';
  if (/iPad/i.test(ua)) return 'iPad (Tablet)';
  if (/Android/i.test(ua)) return 'Android Mobile';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac Desktop (Safari/Chrome)';
  if (/Windows NT/i.test(ua)) return 'Windows PC (Chrome/Edge)';
  if (/Linux/i.test(ua)) return 'Linux Desktop';
  return 'Desktop Browser';
}

// Helper to detect location from browser locale & timezone
export function detectLocation(): { country: string; code: string; flag: string; city: string } {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.includes('Beirut') || tz.includes('Lebanon')) {
      return { country: 'Lebanon', code: 'LB', flag: '🇱🇧', city: 'Beirut' };
    }
    if (tz.includes('Dubai') || tz.includes('Abu_Dhabi') || tz.includes('UAE')) {
      return { country: 'United Arab Emirates', code: 'AE', flag: '🇦🇪', city: 'Dubai' };
    }
    if (tz.includes('Riyadh')) {
      return { country: 'Saudi Arabia', code: 'SA', flag: '🇸🇦', city: 'Riyadh' };
    }
    if (tz.includes('Paris') || tz.includes('Europe/Paris')) {
      return { country: 'France', code: 'FR', flag: '🇫🇷', city: 'Paris' };
    }
    if (tz.includes('New_York') || tz.includes('Los_Angeles') || tz.includes('Chicago') || tz.includes('America/')) {
      return { country: 'United States', code: 'US', flag: '🇺🇸', city: 'New York' };
    }
    if (tz.includes('London') || tz.includes('Europe/London')) {
      return { country: 'United Kingdom', code: 'GB', flag: '🇬🇧', city: 'London' };
    }
    if (tz.includes('Berlin') || tz.includes('Europe/Berlin')) {
      return { country: 'Germany', code: 'DE', flag: '🇩🇪', city: 'Berlin' };
    }
    return { country: 'Lebanon', code: 'LB', flag: '🇱🇧', city: 'Beirut' };
  } catch {
    return { country: 'Lebanon', code: 'LB', flag: '🇱🇧', city: 'Beirut' };
  }
}

// Get or generate session ID
export function getSessionUserId(): string {
  if (typeof window === 'undefined') return 'USR-LIVE-01';
  let sid = sessionStorage.getItem(SESSION_USER_KEY);
  if (!sid) {
    sid = 'USR-' + Math.floor(1000 + Math.random() * 9000);
    sessionStorage.setItem(SESSION_USER_KEY, sid);
  }
  return sid;
}

// Initial clean state
function createCleanAnalyticsStore(): RealtimeAnalyticsStore {
  const today = new Date().toISOString().slice(0, 10);
  const loc = detectLocation();
  const dev = detectDeviceType();

  return {
    totalClicks: 0,
    totalImpressions: 1,
    totalVisitors: 1,
    totalWhatsAppOrders: 0,
    lastResetAt: new Date().toISOString(),
    queries: [],
    pages: [
      { path: 'https://coveristic.net/', name: 'Home & Featured Catalog', clicks: 0, impressions: 1, ctr: 0, position: 1.0, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/phone-cases', name: 'Custom Phone Cases', clicks: 0, impressions: 0, ctr: 0, position: 1.2, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/canvas', name: 'Wall Art Canvas', clicks: 0, impressions: 0, ctr: 0, position: 1.4, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/photo-frames', name: 'Custom Photo Frames', clicks: 0, impressions: 0, ctr: 0, position: 1.5, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/wood-crafts', name: 'Solid Wood Crafts', clicks: 0, impressions: 0, ctr: 0, position: 1.6, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/keychains', name: 'Custom Keychains', clicks: 0, impressions: 0, ctr: 0, position: 1.8, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/mugs', name: 'Ceramic Drinking Mugs', clicks: 0, impressions: 0, ctr: 0, position: 2.0, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/category/tshirts', name: 'Custom T-Shirts & Hoodies', clicks: 0, impressions: 0, ctr: 0, position: 2.1, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/custom-print', name: 'Custom Print Studio & FAQs', clicks: 0, impressions: 0, ctr: 0, position: 2.2, lastVisited: new Date().toISOString() },
      { path: 'https://coveristic.net/about', name: 'About Coveristic & Guarantee', clicks: 0, impressions: 0, ctr: 0, position: 2.5, lastVisited: new Date().toISOString() }
    ],
    days: [
      { date: today, clicks: 0, impressions: 1, ctr: 0, position: 1.0, visitors: 1, whatsAppOrders: 0 }
    ],
    deviceClicks: {
      mobile: dev === 'mobile' ? 0 : 0,
      desktop: dev === 'desktop' ? 0 : 0,
      tablet: dev === 'tablet' ? 0 : 0,
    },
    countryVisitors: {
      [loc.code]: {
        country: loc.country,
        code: loc.code,
        flag: loc.flag,
        visitors: 1,
        clicks: 0,
        impressions: 1
      }
    },
    recentEvents: []
  };
}

// Load real-time analytics
export function getRealtimeAnalytics(): RealtimeAnalyticsStore {
  if (typeof window === 'undefined') return createCleanAnalyticsStore();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const initial = createCleanAnalyticsStore();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
      return initial;
    }
    const parsed = JSON.parse(raw);
    if (!parsed.recentEvents) parsed.recentEvents = [];
    if (typeof parsed.totalWhatsAppOrders !== 'number') parsed.totalWhatsAppOrders = 0;
    return parsed;
  } catch {
    return createCleanAnalyticsStore();
  }
}

// Save real-time analytics & notify listeners
export function saveRealtimeAnalytics(store: RealtimeAnalyticsStore): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    window.dispatchEvent(new CustomEvent('coveristic_analytics_updated', { detail: store }));
  } catch (err) {
    console.warn('Failed to save analytics', err);
  }
}

// Reset analytics
export function resetRealtimeAnalytics(): RealtimeAnalyticsStore {
  const clean = createCleanAnalyticsStore();
  saveRealtimeAnalytics(clean);
  try {
    localStorage.removeItem(SESSIONS_STORAGE_KEY);
  } catch {}
  return clean;
}

// Get genuine active sessions within the last 15 minutes
export function getGenuineActiveSessions(): RealtimeSessionUser[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SESSIONS_STORAGE_KEY);
    const sessions: RealtimeSessionUser[] = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    // Keep active within 15 minutes
    const valid = sessions.filter(s => (now - s.lastActiveTimestamp) < 15 * 60 * 1000);
    return valid;
  } catch {
    return [];
  }
}

// Track genuine session heartbeat
export function updateSessionHeartbeat(action: string, page: string, pageName: string): void {
  if (typeof window === 'undefined') return;
  try {
    const sid = getSessionUserId();
    const loc = detectLocation();
    const dev = detectDeviceType();
    const devLabel = detectDeviceLabel();
    const now = Date.now();

    const currentSessions = getGenuineActiveSessions();
    const existingIdx = currentSessions.findIndex(s => s.id === sid);

    const updatedUser: RealtimeSessionUser = {
      id: sid,
      city: `${loc.city}`,
      country: loc.country,
      flag: loc.flag,
      device: devLabel,
      deviceType: dev,
      page,
      pageName,
      referrerQuery: 'coveristic direct',
      timeOnSite: 'Active now',
      activeAction: action,
      lastActiveTimestamp: now
    };

    if (existingIdx >= 0) {
      currentSessions[existingIdx] = updatedUser;
    } else {
      currentSessions.unshift(updatedUser);
    }

    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(currentSessions));
  } catch {}
}

// Track a real page view
export function trackPageView(rawPath: string, pageName?: string): void {
  const store = getRealtimeAnalytics();
  const path = rawPath.startsWith('http') ? rawPath : `https://coveristic.net${rawPath.startsWith('/') ? rawPath : '/' + rawPath}`;
  const today = new Date().toISOString().slice(0, 10);
  const loc = detectLocation();
  const dev = detectDeviceType();

  store.totalImpressions += 1;

  // Update page record
  const existingPage = store.pages.find(p => p.path === path || p.name === pageName);
  if (existingPage) {
    existingPage.impressions += 1;
    existingPage.lastVisited = new Date().toISOString();
    existingPage.ctr = Number(((existingPage.clicks / Math.max(existingPage.impressions, 1)) * 100).toFixed(2));
  } else {
    store.pages.push({
      path,
      name: pageName || rawPath,
      clicks: 0,
      impressions: 1,
      ctr: 0,
      position: 1.0,
      lastVisited: new Date().toISOString()
    });
  }

  // Update today's daily log
  let dayRecord = store.days.find(d => d.date === today);
  if (!dayRecord) {
    dayRecord = { date: today, clicks: 0, impressions: 0, ctr: 0, position: 1.0, visitors: 1, whatsAppOrders: 0 };
    store.days.unshift(dayRecord);
  }
  dayRecord.impressions += 1;
  dayRecord.ctr = Number(((dayRecord.clicks / Math.max(dayRecord.impressions, 1)) * 100).toFixed(2));

  // Log recent event
  store.recentEvents.unshift({
    id: `ev-${Date.now()}`,
    timestamp: new Date().toISOString(),
    type: 'page_view',
    title: `Viewed Page: ${pageName || rawPath}`,
    details: path,
    device: dev,
    city: loc.city
  });
  if (store.recentEvents.length > 50) store.recentEvents = store.recentEvents.slice(0, 50);

  updateSessionHeartbeat(`Viewing ${pageName || rawPath}`, rawPath, pageName || rawPath);
  saveRealtimeAnalytics(store);
}

// Track a real click event
export function trackRealClick(type: 'product' | 'category' | 'whatsapp' | 'navigation', label: string, pagePath?: string): void {
  const store = getRealtimeAnalytics();
  const today = new Date().toISOString().slice(0, 10);
  const dev = detectDeviceType();
  const loc = detectLocation();

  store.totalClicks += 1;
  store.deviceClicks[dev] = (store.deviceClicks[dev] || 0) + 1;

  if (pagePath) {
    const fullPath = pagePath.startsWith('http') ? pagePath : `https://coveristic.net${pagePath.startsWith('/') ? pagePath : '/' + pagePath}`;
    const page = store.pages.find(p => p.path === fullPath);
    if (page) {
      page.clicks += 1;
      page.ctr = Number(((page.clicks / Math.max(page.impressions, 1)) * 100).toFixed(2));
    }
  }

  let dayRecord = store.days.find(d => d.date === today);
  if (!dayRecord) {
    dayRecord = { date: today, clicks: 0, impressions: 0, ctr: 0, position: 1.0, visitors: 1, whatsAppOrders: 0 };
    store.days.unshift(dayRecord);
  }
  dayRecord.clicks += 1;
  dayRecord.ctr = Number(((dayRecord.clicks / Math.max(dayRecord.impressions, 1)) * 100).toFixed(2));

  if (store.countryVisitors[loc.code]) {
    store.countryVisitors[loc.code].clicks += 1;
  }

  // Log recent event
  store.recentEvents.unshift({
    id: `ev-${Date.now()}`,
    timestamp: new Date().toISOString(),
    type: type === 'product' ? 'product_click' : type === 'whatsapp' ? 'whatsapp_order' : 'category_filter',
    title: `${type === 'product' ? 'Product Click' : type === 'whatsapp' ? 'WhatsApp Order Click' : 'Filter Click'}: ${label}`,
    details: pagePath || '',
    device: dev,
    city: loc.city
  });
  if (store.recentEvents.length > 50) store.recentEvents = store.recentEvents.slice(0, 50);

  updateSessionHeartbeat(`Clicked ${label}`, pagePath || '/', label);
  saveRealtimeAnalytics(store);
}

// Track real search queries
export function trackSearchQuery(queryText: string): void {
  const trimmed = queryText.trim().toLowerCase();
  if (!trimmed || trimmed.length < 2) return;

  const store = getRealtimeAnalytics();
  const loc = detectLocation();
  const dev = detectDeviceType();
  const existing = store.queries.find(q => q.query.toLowerCase() === trimmed);

  if (existing) {
    existing.impressions += 1;
    existing.lastSearched = new Date().toISOString();
  } else {
    store.queries.unshift({
      query: trimmed,
      clicks: 0,
      impressions: 1,
      ctr: 0,
      position: 1.0,
      lastSearched: new Date().toISOString()
    });
  }

  store.recentEvents.unshift({
    id: `ev-${Date.now()}`,
    timestamp: new Date().toISOString(),
    type: 'search',
    title: `Search Query: "${trimmed}"`,
    details: `Visitors searched for "${trimmed}"`,
    device: dev,
    city: loc.city
  });
  if (store.recentEvents.length > 50) store.recentEvents = store.recentEvents.slice(0, 50);

  updateSessionHeartbeat(`Searched for "${trimmed}"`, '/shop', `Search: ${trimmed}`);
  saveRealtimeAnalytics(store);
}

// Track WhatsApp Order Conversions
export function trackWhatsAppOrderConversion(productTitle: string, price: number): void {
  const store = getRealtimeAnalytics();
  const today = new Date().toISOString().slice(0, 10);
  const loc = detectLocation();
  const dev = detectDeviceType();

  store.totalWhatsAppOrders = (store.totalWhatsAppOrders || 0) + 1;
  store.totalClicks += 1;

  let dayRecord = store.days.find(d => d.date === today);
  if (!dayRecord) {
    dayRecord = { date: today, clicks: 1, impressions: 1, ctr: 100, position: 1.0, visitors: 1, whatsAppOrders: 1 };
    store.days.unshift(dayRecord);
  } else {
    dayRecord.whatsAppOrders = (dayRecord.whatsAppOrders || 0) + 1;
    dayRecord.clicks += 1;
  }

  store.recentEvents.unshift({
    id: `ev-${Date.now()}`,
    timestamp: new Date().toISOString(),
    type: 'whatsapp_order',
    title: `WhatsApp Order Generated: ${productTitle}`,
    details: `Order sent to WhatsApp +961 81773349 ($${price})`,
    device: dev,
    city: loc.city
  });
  if (store.recentEvents.length > 50) store.recentEvents = store.recentEvents.slice(0, 50);

  updateSessionHeartbeat(`Ordered ${productTitle} on WhatsApp`, '/order', productTitle);
  saveRealtimeAnalytics(store);
}
